// GroupCardActions — the ⋮ on a group card: Open details · a "Move to section"
// list (every section, the group's current one check-marked) · Delete group.
// The section list is inline rather than a picker dialog, so a move is one
// click. Presentational; GroupsTab owns the flows. The consumer must wrap this
// in a stopPropagation span — the card itself is a clickable role='button'.
// Move/Delete are OPTIONAL: the page passes `undefined` when the verb is
// missing and the block (with its divider) doesn't render — absence means
// hidden. Open details is the permanent read item, so the kebab never empties.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';
import type { GroupSection } from '@/api/endpoints/groupsApi';

interface GroupCardActionsProps {
  /** Group name — labels the trigger for screen readers. */
  groupName: string;
  /** Every section, default included — the move rows. */
  sections: GroupSection[];
  /** The group's current section; gets the check and isn't a move target. */
  currentSectionId: string;
  onOpenDetails: () => void;
  /** Absent = hidden. */
  onMoveToSection?: (sectionId: string) => void;
  /** Absent = hidden. */
  onDelete?: () => void;
}

const GroupCardActions = ({
  groupName,
  sections,
  currentSectionId,
  onOpenDetails,
  onMoveToSection,
  onDelete,
}: GroupCardActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Close before running, so the menu isn't left hanging over a confirm.
  const pick = (run: () => void) => {
    setOpen(false);
    run();
  };

  const item = (
    label: string,
    icon: string,
    onClick: () => void,
    opts: { danger?: boolean } = {},
  ): ReactNode => (
    <button
      type='button'
      className={`app-dropdown__item${opts.danger ? ' app-dropdown__item--danger' : ''}`}
      role='menuitem'
      onClick={() => pick(onClick)}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  const sectionRow = (section: GroupSection): ReactNode => {
    const current = section.id === currentSectionId;
    return (
      <button
        key={section.id}
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        aria-current={current || undefined}
        // The current section is a no-op — it stays listed to show where the
        // group lives.
        onClick={() => pick(() => !current && onMoveToSection?.(section.id))}
      >
        <Icon name={section.isDefault ? 'inbox' : 'folder'} size={15} />
        <span className='app-dropdown__item-label'>{section.name}</span>
        {current && <Icon name='check' size={15} className='groups-menu__check' />}
      </button>
    );
  };

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='end'
      width={220}
      portal
      trigger={
        <IconButton
          icon='ellipsis-vertical'
          variant='ghost'
          aria-label={`${groupName} — ${t('people_col_actions')}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        />
      }
    >
      {item(t('groups_action_open_details'), 'panel-left-close', onOpenDetails)}

      {onMoveToSection && (
        <>
          <div className='app-dropdown__divider' role='separator' />
          <div className='app-dropdown__header groups-menu__head'>{t('groups_action_move')}</div>
          {sections.map(sectionRow)}
        </>
      )}

      {onDelete && (
        <>
          <div className='app-dropdown__divider' role='separator' />
          {item(t('groups_action_delete_group'), 'trash-2', onDelete, { danger: true })}
        </>
      )}
    </Dropdown>
  );
};

export default GroupCardActions;
